import React, { memo } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import LoadingSpinner from "../LoadingSpinner";
import Avatar from "../Avatar";
import {
  canDeleteEvent,
  canEditEvent,
  canOpenOpenCallForm,
} from "../../core/utils/permissionUtils";
import {
  getDepartmentColor,
  getDepartmentIcon,
  getDepartmentId,
  getDepartmentName,
} from "../../core/constants/departments";
import { userIdKey } from "../hooks/useFetchUsersByIds";

export const getEventId = (event) => (event?._id == null ? "" : String(event._id));

const EventListSection = memo(
  ({
    loading,
    events,
    paginatedEvents,
    selectedEventId,
    toggleEventInfo,
    contextUser,
    handleDeleteEvent,
    formatDate,
    currentPage = 1,
    totalPages = 1,
    goToNextPage,
    goToPreviousPage,
    sortedEvents = [],
    getUserDisplayName,
    getUserById,
    catalog,
    showPagination = true,
    emptyMessage = "No events match your search criteria",
  }) => {
    return (
      <div className="user-list-main-section">
        {loading && !events.length ? (
          <LoadingSpinner message="Loading events" size="sm" />
        ) : (
          <ul className="events-list">
            {paginatedEvents.length > 0 ? (
              paginatedEvents.map((event) => (
                <li
                  key={getEventId(event)}
                  className={`event-item ${selectedEventId === getEventId(event) ? "selected" : ""}`}
                  onClick={() => toggleEventInfo(getEventId(event))}
                  style={{
                    "--department-color": getDepartmentColor(
                      event.departments?.[0],
                      catalog,
                    ),
                  }}
                >
                  <div className="event-item-banner">
                    {event.departments?.length > 0 ? (
                      event.departments.map((dept) => {
                        const deptColor =
                          getDepartmentColor(dept, catalog) || "#6b7280";
                        const deptKey =
                          getDepartmentId(dept) || getDepartmentName(dept);
                        return (
                          <div
                            key={deptKey}
                            className="event-item-banner-segment"
                            style={{
                              backgroundColor: `color-mix(in srgb, ${deptColor} 15%, transparent)`,
                            }}
                          >
                            {getDepartmentIcon(
                              dept,
                              {
                                className: "event-item-banner-icon",
                                style: { color: deptColor },
                              },
                              catalog,
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="event-item-banner-segment" />
                    )}
                  </div>
                  <div className="event-item-visible-section">
                    <div className="visible-section-info">
                      <div className="event-name">
                        {event.name}
                        {event.isGeneralMeeting && (
                          <span className="general-meeting-badge">General Meeting</span>
                        )}
                      </div>
                      <div className="event-date-time">
                        {formatDate(event.date)}
                      </div>
                      <div className="event-location">{event.meetingPoint}</div>
                    </div>

                    <div className="visible-section-buttons">
                      {!event.isGeneralMeeting && (
                        <Link
                          to={`/events/${getEventId(event)}/counter`}
                          className="event-list-button"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon
                            icon="mdi:counter"
                            className="icon"
                            style={{ color: "black" }}
                          />
                        </Link>
                      )}

                      {!event.isGeneralMeeting && canOpenOpenCallForm(contextUser, event) && (
                        <Link
                          to={`/events/opencall/${getEventId(event)}`}
                          className="event-list-button"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon
                            icon="bxs:megaphone"
                            className="icon"
                            style={{ color: "black" }}
                          />
                        </Link>
                      )}

                      {canEditEvent(contextUser, event) && (
                        <div className="oc-section-buttons">
                          <Link
                            to={`/events/${getEventId(event)}`}
                            className="event-list-button"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Icon
                              icon="bxs:edit"
                              className="icon"
                              style={{ color: "black" }}
                            />
                          </Link>
                        </div>
                      )}

                      {canDeleteEvent(contextUser, event) && (
                        <button
                          type="button"
                          className="event-list-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(getEventId(event));
                          }}
                        >
                          <Icon
                            icon="solar:trash-bin-minimalistic-broken"
                            className="icon"
                            style={{ color: "#971717" }}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                  {selectedEventId === getEventId(event) && (
                    <div className="event-item-hidden-section">
                      {canEditEvent(contextUser, event) &&
                        (event.taiga ||
                          event.budget ||
                          event.eventupp ||
                          event.esnActivities ||
                          event.openCallSpread) && (
                          <div className="event-links-section">
                            {event.taiga && (
                              <div className="event-link">
                                <Icon
                                  icon="logos:taiga"
                                  style={{ marginRight: "6px" }}
                                />
                                Taiga:{" "}
                                <a
                                  href={event.taiga}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Link
                                </a>
                              </div>
                            )}
                            {event.budget && (
                              <div className="event-link">
                                <Icon
                                  icon="lucide:wallet"
                                  style={{ marginRight: "6px" }}
                                />
                                Reports:{" "}
                                <a
                                  href={event.budget}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Link
                                </a>
                              </div>
                            )}
                            {event.eventupp && (
                              <div className="event-link">
                                <Icon
                                  icon="material-symbols:sell"
                                  style={{ marginRight: "6px" }}
                                />
                                EventUpp:{" "}
                                <a
                                  href={event.eventupp}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Link
                                </a>
                              </div>
                            )}
                            {event.esnActivities && (
                              <div className="event-link">
                                <Icon
                                  icon="mdi:earth"
                                  style={{ marginRight: "6px" }}
                                />
                                ESN Activities:{" "}
                                <a
                                  href={event.esnActivities}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Link
                                </a>
                              </div>
                            )}
                            {event.openCallSpread && (
                              <div className="event-link">
                                <Icon
                                  icon="garden:file-spreadsheet-fill-12"
                                  style={{ marginRight: "6px" }}
                                />
                                Open Call Spread:{" "}
                                <a
                                  href={event.openCallSpread}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Link
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      <div className="organizers-info">
                        <div className="organizer-label">
                          <Icon icon="fluent-mdl2:party-leader" />
                          MOs:
                        </div>
                        <div className="organizer-values">
                          {event.mos && event.mos.length > 0 ? (
                            event.mos.map((moId) => (
                              <Link
                                key={moId}
                                className="user-link"
                                to={`/users/${moId}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Avatar user={getUserById(moId)} />
                                <span className="user-link-text">
                                  {getUserDisplayName(moId)}
                                </span>
                              </Link>
                            ))
                          ) : (
                            <span>No MOs assigned</span>
                          )}
                        </div>
                      </div>
                      <div className="organizers-info">
                        <div className="organizer-label">
                          <Icon icon="carbon:events" />
                          VMO:
                        </div>
                        <div className="organizer-values">
                          {event.vmos && event.vmos.length > 0 ? (
                            event.vmos.map((vmoId) => (
                              <Link
                                key={vmoId}
                                className="user-link"
                                to={`/users/${vmoId}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Avatar user={getUserById(vmoId)} />
                                <span className="user-link-text">
                                  {getUserDisplayName(vmoId)}
                                </span>
                              </Link>
                            ))
                          ) : (
                            <span>No VMO</span>
                          )}
                        </div>
                      </div>
                      <div className="organizers-info">
                        <div className="organizer-label">
                          <Icon icon="mdi:account-group" />
                          Staff:
                        </div>
                        <div className="organizer-values">
                          {event.participants && event.participants.length > 0 ? (
                            event.participants.map((participant) => (
                              <Link
                                key={participant}
                                className="user-link"
                                to={`/users/${participant}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Avatar user={getUserById(participant)} />
                                <span className="user-link-text">
                                  {getUserDisplayName(participant)}
                                </span>
                              </Link>
                            ))
                          ) : (
                            <span>No participants</span>
                          )}
                        </div>
                      </div>
                      <div className="organizers-info">
                        <div className="organizer-label">
                          <Icon icon="tdesign:wave-bye" />
                          Alumni:
                        </div>
                        <div className="organizer-values">
                          {event.alumni && event.alumni.length > 0 ? (
                            event.alumni.map((alumniRef) => {
                              const alumniId = userIdKey(alumniRef);
                              return (
                              <Link
                                key={alumniId}
                                className="user-link"
                                to={`/users/${alumniId}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Avatar user={getUserById(alumniId)} />
                                <span className="user-link-text">
                                  {getUserDisplayName(alumniId)}
                                </span>
                              </Link>
                              );
                            })
                          ) : (
                            <span>No alumni</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              ))
            ) : (
              <li
                className="no-results"
                style={{ textAlign: "center", padding: "20px" }}
              >
                <p>{emptyMessage}</p>
                <Icon
                  icon="ph:empty"
                  className="icon"
                  style={{ width: "30px", height: "30px" }}
                />
              </li>
            )}
          </ul>
        )}
        {showPagination && (
          <div
            className="pagination-controls"
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "20px 0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <button
                type="button"
                className="button"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                style={{
                  opacity: currentPage === 1 ? 0.5 : 1,
                  margin: "0",
                  maxWidth: "100px",
                  height: "20px",
                }}
              >
                <i className="uil uil-angle-left-b"></i> Previous
              </button>

              <span
                style={{
                  padding: "8px 16px",
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.15)",
                  fontSize: "14px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  lineHeight: "20px",
                }}
              >
                Page {currentPage} of {totalPages || 1}
              </span>

              <button
                type="button"
                className="button"
                onClick={goToNextPage}
                disabled={
                  currentPage === totalPages || sortedEvents.length === 0
                }
                style={{
                  opacity:
                    currentPage === totalPages || sortedEvents.length === 0
                      ? 0.5
                      : 1,
                  margin: "0",
                  maxWidth: "100px",
                  height: "20px",
                }}
              >
                Next <i className="uil uil-angle-right-b"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default EventListSection;
